/**
 * <galaxy-carousel>: a scroll-snap row with previous / next buttons.
 * Expects [data-carousel-track] (the scrolling list) and [data-carousel-prev] / [data-carousel-next] buttons
 * anywhere inside. The buttons move by one visible page and are disabled at the ends.
 * Optional [data-carousel-dots]: one .galaxy-dash button per item (phones); the dash of the item at the
 * start of the row is marked, and tapping a dash scrolls to its item.
 * Photos further along the row are lazy and would only start loading once swiped into view, so the
 * card shows up empty for a moment; when the row comes near the screen they are all loaded at once.
 */
class GalaxyCarousel extends HTMLElement {
  #controller = new AbortController();
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.#go(-1), { signal });
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.#go(1), { signal });
    this.querySelector('[data-carousel-dots]')?.addEventListener('click', this.#goTo, { signal });
    this.#track.addEventListener('scroll', this.#update, { passive: true, signal });
    window.addEventListener('resize', this.#update, { signal });
    this.#update();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        for (const img of this.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
        this.#observer?.disconnect();
      },
      { rootMargin: '400px 0px' }
    );
    this.#observer.observe(this.#track);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  /** @param {number} direction */
  #go(direction) {
    const track = this.#track;
    if (!track) return;
    const item = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = item instanceof HTMLElement ? item.offsetWidth + gap : track.clientWidth;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * rtl * step * perPage, behavior: 'smooth' });
  }

  #update = () => {
    const track = this.#track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = pos <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = pos >= max - 2;
    this.toggleAttribute('data-static', max <= 2);

    const dots = this.querySelector('[data-carousel-dots]');
    if (!(dots instanceof HTMLElement)) return;
    dots.hidden = max <= 2;
    if (dots.hidden) return;
    // The last item snaps to the end (inside the row's end padding), so it counts once it is fully in view.
    const items = [...track.children];
    const box = track.getBoundingClientRect();
    let index = items.length - 1;
    if (pos < max - 2 && (items.at(-1)?.getBoundingClientRect().right ?? 0) > box.right + 2) {
      const distances = items.map((item) => Math.abs(item.getBoundingClientRect().left - box.left));
      index = distances.indexOf(Math.min(...distances));
    }
    [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  };

  /** @param {Event} event */
  #goTo = (event) => {
    const track = this.#track;
    const dot = event.target instanceof Element ? event.target.closest('[data-go]') : null;
    const item = track?.children[Number(dot?.getAttribute('data-go'))];
    if (!track || !item) return;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({
      left: item.getBoundingClientRect().left - track.getBoundingClientRect().left,
      behavior: smooth ? 'smooth' : 'auto',
    });
  };
}

if (!customElements.get('galaxy-carousel')) {
  customElements.define('galaxy-carousel', GalaxyCarousel);
}
